<script lang="ts">
  import DropdownListItem from './DropdownListItem.svelte';
  import { DividerKey } from '$shared/constants/global';
  import {
    NavRowKind,
    NoHighlight,
    createTypeAhead,
    getSelectableIndices,
    isTypeAheadKey,
    moveHighlight,
    scrollRowIntoView,
    type NavRow
  } from '$shared/helpers/listNavigation';

  interface Props {
    list: CommonList[];
    maxheight?: boolean;
    onclick?: (item: CommonList) => void;
    /** Reports the highlighted option's DOM id so the focus holder (the
     *  dropdown trigger) can point aria-activedescendant at it. */
    onhighlight?: (optionId?: string) => void;
    /** Supplied by Dropdown so its trigger can aria-controls this list. */
    listId?: string;
  }

  let { list, maxheight = false, onclick, onhighlight, listId }: Props = $props();

  const fallbackId = $props.id();
  const ownListId = $derived(listId || fallbackId);

  const optionId = (index: number): string => `${ownListId}-opt-${index}`;

  let container: HTMLElement | undefined = $state();
  let highlightedIndex = $state(NoHighlight);

  const typeAhead = createTypeAhead();

  let rows: NavRow[] = $derived(
    list.map((item) => ({
      kind: item.id === DividerKey ? NavRowKind.Divider : NavRowKind.Item,
      item,
      selectable: item.id !== DividerKey && !item.disabled
    }))
  );

  let selectableIndices = $derived(getSelectableIndices(rows));

  export const reset = (): void => {
    highlightedIndex = NoHighlight;
    typeAhead.reset();
    onhighlight?.(undefined);
  };

  const setHighlight = (index: number): void => {
    if (index === NoHighlight) return;
    highlightedIndex = index;
    scrollRowIntoView(container, index);
    onhighlight?.(optionId(index));
  };

  /** Returns true when the key was consumed. */
  export const handleKeyDown = (e: KeyboardEvent): boolean => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      setHighlight(
        moveHighlight(highlightedIndex, selectableIndices, e.key === 'ArrowDown' ? 1 : -1)
      );
      return true;
    }
    if (e.key === 'Home' || e.key === 'End') {
      setHighlight(moveHighlight(NoHighlight, selectableIndices, e.key === 'Home' ? 1 : -1));
      return true;
    }
    if (e.key === 'Enter') {
      const row = rows[highlightedIndex];
      if (!row?.selectable) return false;
      onclick?.(row.item);
      return true;
    }
    if (isTypeAheadKey(e)) {
      setHighlight(typeAhead.match(e.key, rows, highlightedIndex));
      return true;
    }
    return false;
  };
</script>

<div bind:this={container} class="plain-list" class:maxheight role="listbox" id={ownListId}>
  {#each rows as row, index}
    <DropdownListItem
      item={row.item}
      navIndex={index}
      optionId={optionId(index)}
      highlighted={index === highlightedIndex}
      onclick={() => onclick?.(row.item)}
    />
  {/each}
</div>

<style>
  .plain-list {
    width: 100%;
    max-height: 300px;
    overflow: auto;
    /* Anchors offsetTop of the rows for scrollRowIntoView. */
    position: relative;
  }
  .plain-list.maxheight {
    max-height: 100%;
  }
</style>
